import { InvalidUrlError } from '@execonline-inc/url';
import { AppyError } from '../Appy';
import { MissingLinkError } from '../LinkyLinky';
import { GroupResource } from '../GroupsStore/Types';
import { Resource } from '../Resource/Types';
import { Error, ServerError } from '../ErrorHandling';
import { License } from '../components/People/Participants/AddParticipant/Stores/Types';

export type NewGroupError = AppyError | InvalidUrlError | MissingLinkError | Error | ServerError;

/**
 * Available learning collection returned by the new-group endpoint.
 * `selected` is always false for a not-yet-created group, but the shape
 * matches Edit exactly so a single decoder + section component can serve
 * both flows (a future Edit Group re-styling story will collapse them).
 */
export type AccessSource = 'organization' | 'client_group' | 'private';

export interface AvailableCollection {
  id: number;
  name: string;
  experiencesCount: number;
  selected: boolean;
  accessSource: AccessSource;
}

/**
 * Available product license returned by the new-group endpoint. Extends
 * `License` (the shape already used by AddParticipant) with a `selected`
 * flag so we can seed the picker from the payload if the server ever
 * pre-selects licenses (currently it never does for new groups).
 */
export interface AvailableLicense extends License {
  selected: boolean;
}

export interface NewGroupPayload {
  learningCollections: AvailableCollection[];
  productLicenses: AvailableLicense[];
}

export type NewGroupResource = Resource<NewGroupPayload>;

export interface Waiting {
  kind: 'waiting';
}

export interface Loading {
  kind: 'loading';
}

export interface LoadError {
  kind: 'load-error';
  error: NewGroupError;
}

export interface Ready {
  kind: 'ready';
  resource: NewGroupResource;
}

export interface Submitting {
  kind: 'submitting';
  resource: NewGroupResource;
}

export interface SubmitError {
  kind: 'submit-error';
  resource: NewGroupResource;
  error: NewGroupError;
}

export interface Success {
  kind: 'success';
  resource: NewGroupResource;
  createdGroup: GroupResource;
}

export type State = Waiting | Loading | LoadError | Ready | Submitting | SubmitError | Success;

export const waiting = (): Waiting => ({ kind: 'waiting' });
export const loading = (): Loading => ({ kind: 'loading' });
export const loadError = (error: NewGroupError): LoadError => ({ kind: 'load-error', error });
export const ready = (resource: NewGroupResource): Ready => ({ kind: 'ready', resource });
export const submitting = (resource: NewGroupResource): Submitting => ({
  kind: 'submitting',
  resource,
});
export const submitError = (resource: NewGroupResource, error: NewGroupError): SubmitError => ({
  kind: 'submit-error',
  resource,
  error,
});
export const success = (resource: NewGroupResource, createdGroup: GroupResource): Success => ({
  kind: 'success',
  resource,
  createdGroup,
});
